import type HlsType from 'hls.js';
import type { StreamQuality } from '../shared/api-types.js';

/**
 * Live playback.
 *
 * Two playback paths:
 *  - iOS/Safari play HLS natively (and are the only browsers that reliably
 *    decode the camera's HEVC high-definition feed), so we hand the playlist
 *    straight to the <video> element.
 *  - Everywhere else uses hls.js over Media Source Extensions. MSE support for
 *    HEVC is patchy, so we fall back to the H.264 SD feed unless the browser
 *    says it can decode HEVC.
 */

export type PlayerStatus = 'connecting' | 'live' | 'stalled' | 'unavailable';

export type Player = {
  readonly quality: StreamQuality;
  setQuality(quality: StreamQuality): void;
  destroy(): void;
};

const HEVC_MSE_CANDIDATES = [
  'video/mp4; codecs="hvc1.1.6.L93.B0"',
  'video/mp4; codecs="hvc1.1.6.L120.B0"',
  'video/mp4; codecs="hev1.1.6.L93.B0"',
];

const supportsNativeHls = (video: HTMLVideoElement): boolean =>
  video.canPlayType('application/vnd.apple.mpegurl') !== '';

const supportsHevcInMse = (): boolean => {
  if (typeof MediaSource === 'undefined') return false;
  return HEVC_MSE_CANDIDATES.some((type) => {
    try {
      return MediaSource.isTypeSupported(type);
    } catch {
      return false;
    }
  });
};

/** The best quality this browser can actually decode. */
export const preferredQuality = (video: HTMLVideoElement): StreamQuality => {
  if (supportsNativeHls(video)) return 'hd';
  return supportsHevcInMse() ? 'hd' : 'sd';
};

const playlistUrl = (quality: StreamQuality): string =>
  `/api/live/playlist.m3u8?quality=${quality}&t=${String(Date.now())}`;

const HLS_CONFIG = {
  lowLatencyMode: true,
  // The HD feed publishes 4s segments; stay a couple of segments back from the
  // live edge so a single slow fetch does not stall playback.
  liveSyncDuration: 8,
  liveMaxLatencyDuration: 30,
  maxBufferLength: 12,
  backBufferLength: 30,
  manifestLoadingTimeOut: 15_000,
  manifestLoadingMaxRetry: 4,
  levelLoadingTimeOut: 15_000,
  fragLoadingTimeOut: 25_000,
} as const;

export type PlayerOptions = {
  readonly video: HTMLVideoElement;
  readonly onStatus: (status: PlayerStatus) => void;
  readonly initialQuality?: StreamQuality;
};

export const createPlayer = (options: PlayerOptions): Player => {
  const { video, onStatus } = options;
  let quality: StreamQuality = options.initialQuality ?? preferredQuality(video);
  let hls: HlsType | null = null;
  let destroyed = false;
  let recoverAttempts = 0;
  /** Populated by the lazy import; hls.js is ~500 kB and only the live page needs it. */
  let HlsCtor: typeof HlsType | null = null;

  const attachVideoEvents = (): void => {
    video.addEventListener('playing', () => onStatus('live'));
    video.addEventListener('waiting', () => onStatus('stalled'));
    video.addEventListener('stalled', () => onStatus('stalled'));
    video.addEventListener('error', () => onStatus('unavailable'));
  };

  const startNative = (): void => {
    video.src = playlistUrl(quality);
    video.load();
    void video.play().catch(() => {
      // Autoplay can be refused; the poster/controls still let the user start.
    });
  };

  const handleFatalError = (data: { type: string }): void => {
    if (destroyed || hls === null || HlsCtor === null) return;

    if (data.type === HlsCtor.ErrorTypes.NETWORK_ERROR) {
      onStatus('stalled');
      hls.startLoad();
      return;
    }
    if (data.type === HlsCtor.ErrorTypes.MEDIA_ERROR && recoverAttempts < 3) {
      recoverAttempts += 1;
      onStatus('stalled');
      hls.recoverMediaError();
      return;
    }
    onStatus('unavailable');
    hls.destroy();
    hls = null;
  };

  const startMse = async (): Promise<void> => {
    if (HlsCtor === null) {
      try {
        HlsCtor = (await import('hls.js')).default;
      } catch {
        onStatus('unavailable');
        return;
      }
    }
    // The user may have navigated away while the chunk downloaded.
    if (destroyed) return;

    if (!HlsCtor.isSupported()) {
      onStatus('unavailable');
      return;
    }

    const instance = new HlsCtor(HLS_CONFIG);
    hls = instance;

    instance.on(HlsCtor.Events.MANIFEST_PARSED, () => {
      void video.play().catch(() => undefined);
    });
    instance.on(HlsCtor.Events.FRAG_BUFFERED, () => {
      recoverAttempts = 0;
      if (!video.paused) onStatus('live');
    });
    instance.on(HlsCtor.Events.ERROR, (_event, data) => {
      if (data.fatal) handleFatalError(data);
    });

    instance.loadSource(playlistUrl(quality));
    instance.attachMedia(video);
  };

  const start = (): void => {
    onStatus('connecting');
    if (supportsNativeHls(video)) startNative();
    else void startMse();
  };

  const teardown = (): void => {
    if (hls !== null) {
      hls.destroy();
      hls = null;
    }
    video.removeAttribute('src');
    video.load();
  };

  attachVideoEvents();
  start();

  return {
    get quality() {
      return quality;
    },
    setQuality(next) {
      if (next === quality || destroyed) return;
      quality = next;
      teardown();
      start();
    },
    destroy() {
      destroyed = true;
      teardown();
    },
  };
};
